import { EventType, useI18N, useResponsive } from "@sundae/react-hooks";
import { NumberI18N, Text } from "@sundae/ui-toolkit";
import { ADA_METADATA, SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import merge from "lodash/merge";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import Marquee from "react-fast-marquee";

import { useCalculateContributionOutcome } from "../../../hooks/taste-test/useCalculateContributionOutcome";
import { useEventTracking } from "../../../hooks/useEventTracking";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetName } from "../../../utils/assets.utils";
import BalanceWithAssetName from "../../BalanceWithAssetName";

interface ITasteTestKPIsMarqueeProps extends HTMLAttributes<HTMLDivElement> {
  isStickyMarquee?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  marqueeProps?: any; // Marquee Props
  size?: "2xs" | "sm";
  trackInView?: boolean;
  variant?: "muted" | "silent";
}

const TasteTestKPIsMarquee: FC<ITasteTestKPIsMarqueeProps> = ({
  isStickyMarquee = true,
  marqueeProps: marquee,
  size = "2xs",
  trackInView,
  variant = "silent",
  ...rest
}) => {
  const { lg: largeScreen } = useResponsive();
  const [hasBeenTracked, setHasBeenTracked] = useState(false);
  const handleEventTracking = useEventTracking();
  const { currentLocale, t } = useI18N("taste-test");
  const {
    derived: { latestHistoryRecord },
    state: {
      tasteTest,
      tasteTestStatus: { isTasteTestInactive },
    },
  } = useTasteTestContext();
  const { calculateTargetPrice, getCurrentBalanceAssetAmounts } = useCalculateContributionOutcome({
    latestHistoryRecord,
    currentBalance: tasteTest?.currentBalance,
  });

  const defaultMarqueeProps = {
    pauseOnHover: true,
    speed: 30,
    style: {
      WebkitMaskImage:
        "linear-gradient(to left, transparent, rgb(0, 0, 0) 7%, rgb(0, 0, 0) 93%, transparent)",
    },
  };
  const { gatheredAssetAmount, suppliedAssetAmount } = getCurrentBalanceAssetAmounts();
  const targetPrice = calculateTargetPrice();
  const marqueeProps = merge(defaultMarqueeProps, marquee);

  const typedAsset = (tasteTest?.asset as unknown as IAssetMetaData) ?? ({} as IAssetMetaData);

  /**
   * Effect to track the view of the KPIs marquee.
   * We only want to track the view once, so we use the hasBeenTracked state to keep track of that.
   */
  useEffect(() => {
    if (hasBeenTracked && trackInView) {
      return;
    }

    if (trackInView) {
      setHasBeenTracked(true);

      handleEventTracking({
        eventType: EventType.View,
        source: "TasteTestKPIsMarquee",
        value: "show",
      });
    }
  }, [handleEventTracking, hasBeenTracked, trackInView]);

  return (
    <div
      className={cx(
        "w-full",
        {
          "mt-2 h-4": isStickyMarquee,
        },
        rest.className
      )}
    >
      <Marquee {...marqueeProps}>
        <div
          className={cx("flex items-center", {
            "justify-evenly space-x-4": largeScreen,
            "flex-wrap gap-x-4 gap-y-1": !largeScreen,
          })}
        >
          <div
            className={cx("flex items-center gap-1", {
              "ml-4": largeScreen,
            })}
          >
            <Text tag="span" size={size} variant={variant}>
              {isTasteTestInactive ? t("price", { ns: "common" }) : t("detail.kpis.targetPrice")}:
            </Text>

            <div className="flex items-center gap-[2px]">
              <Text tag="span" variant={variant} size={size}>
                {targetPrice.display}
              </Text>

              <Text tag="span" size={size} variant={variant}>
                {t("per", { ns: "common" })} {getAssetName(typedAsset)}
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Text tag="span" size={size} variant={variant}>
              {t("detail.kpis.currentPoolRatio", {
                context: isTasteTestInactive ? "inactive" : undefined,
              })}
              :
            </Text>

            <div className="flex items-center gap-1">
              <BalanceWithAssetName
                asset={ADA_METADATA}
                showSymbol
                privacy={false}
                locale={currentLocale}
                size={size}
                value={gatheredAssetAmount}
                variant={variant}
              />
              <Text size={size} tag="span" variant={variant}>
                /
              </Text>
              <Text tag="div" className="flex items-center gap-1">
                <BalanceWithAssetName
                  asset={typedAsset}
                  privacy={false}
                  locale={currentLocale}
                  showSymbol={SundaeUtils.isAdaAsset(tasteTest?.asset)}
                  size={size}
                  value={suppliedAssetAmount}
                  variant={variant}
                />
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Text tag="span" size={size} variant={variant}>
              {t("detail.kpis.participants")}:
            </Text>

            <NumberI18N
              locale={currentLocale}
              size={size}
              value={tasteTest?.participants ?? 0}
              variant={variant}
            />
          </div>

          <div className="flex items-center gap-1">
            <Text tag="span" size={size} variant={variant}>
              {t("detail.kpis.interactions")}:
            </Text>

            <NumberI18N
              locale={currentLocale}
              size={size}
              value={tasteTest?.interactions ?? 0}
              variant={variant}
            />
          </div>

          <div className="flex items-center gap-1">
            <Text tag="span" size={size} variant={variant}>
              {t("detail.kpis.tokenSupplied")}:
            </Text>

            <BalanceWithAssetName
              abbreviate={false}
              asset={typedAsset}
              locale={currentLocale}
              showSymbol={SundaeUtils.isAdaAsset(tasteTest?.asset)}
              size={size}
              privacy={false}
              variant={variant}
              value={suppliedAssetAmount}
            />
          </div>
        </div>
      </Marquee>
    </div>
  );
};

export default TasteTestKPIsMarquee;
